import { useEffect, useCallback, useState } from 'react';
import sanitizeHtml from 'sanitize-html-react';
import axios from 'axios';
import { useAppContext } from 'context/app';
import { useHistory } from 'react-router-dom';
import { retryableFn } from 'utils/fns';
import { TravelCategory } from 'interfaces/common';
import { logger } from 'utils';

type TravelReasonResponse = {
  message: string;
  lookups: TravelCategory[];
};

const commonSvgTags = [
  'altGlyph',
  'altGlyphDef',
  'altGlyphItem',
  'animate',
  'animateColor',
  'animateMotion',
  'animateTransform',
  'animation',
  'circle',
  'clipPath',
  'color-profile',
  'cursor',
  'defs',
  'discard',
  'ellipse',
  'feBlend',
  'feColorMatrix',
  'feComponentTransfer',
  'feComposite',
  'feConvolveMatrix',
  'feDiffuseLighting',
  'feDisplacementMap',
  'feDistantLight',
  'feDropShadow',
  'feFlood',
  'feFuncA',
  'feFuncB',
  'feFuncG',
  'feFuncR',
  'feGaussianBlur',
  'feImage',
  'feMerge',
  'feMergeNode',
  'feMorphology',
  'feOffset',
  'fePointLight',
  'feSpecularLighting',
  'feSpotLight',
  'feTile',
  'feTurbulence',
  'filter',
  'font',
  'font-face',
  'font-face-format',
  'font-face-name',
  'font-face-src',
  'font-face-uri',
  'foreignObject',
  'g',
  'glyph',
  'glyphRef',
  'handler',
  'hkern',
  'image',
  'line',
  'linearGradient',
  'listener',
  'marker',
  'mask',
  'mpath',
  'path',
  'pattern',
  'polygon',
  'polyline',
  'prefetch',
  'radialGradient',
  'rect',
  'set',
  'solidColor',
  'stop',
  'svg',
  'symbol',
  'tbreak',
  'text',
  'textArea',
  'textPath',
  'title',
  'tref',
  'tspan',
  'view',
  'vkern'
];

const fetchTravelReasons = retryableFn(async function fetchTravelReasons(transactionId: string) {
  const headers = { 'X-TransactionID': transactionId };
  const response = await axios.get<TravelReasonResponse>(
    `${process.env.REACT_APP_AWS_API_BASE_URL}/lookup/travel-reasons-v2`,
    { headers }
  );
  return response.data;
});

export default function useTravelReasons() {
  const context = useAppContext();
  const history = useHistory();
  const [fetchCount, setFetchCount] = useState(0);

  const fetch = useCallback(() => {
    setFetchCount(c => c + 1);
  }, [setFetchCount]);

  useEffect(() => {
    (async () => {
      try {
        // no need to re-fetch data if we already have it
        if (
          context.currentState.travelReasons.status === 'success' &&
          context.currentState.travelReasons.reasons.every(reason => reason.attestation)
        )
          return;

        context.actions.setTravelReasonsLoading();

        const response = await fetchTravelReasons(context.currentState.transactionID);
        const reasons = response.lookups
          .flatMap(category =>
            category.reasons.map(reason => ({ ...reason, zone: category.category }))
          )
          .filter(item => item.enabled)
          .map(reason => ({
            label: reason.value,
            value: reason.code,
            zone: reason.zone,
            attestation: reason.attestation,
            attestationDeclaration: reason.attestationDeclaration,
            attestationPostDeclaration: reason.attestationPostDeclaration,
            attestationFullyVax: reason.attestationFullyVax
          }));
        const categories = response.lookups
          .filter(category => category.reasons.filter(({ enabled }) => enabled).length > 0)
          .map(category => ({
            ...category,
            icon: sanitizeHtml(category.icon, {
              allowedTags: commonSvgTags,
              allowedAttributes: false
            })
          }));

        context.actions.setTravelReasons(reasons, categories);
        if (fetchCount > 0) {
          logger.info('TravelReasons resolved through retry');
        }
      } catch (error: any) {
        logger.error('Fetch travel reason error', error);
        context.actions.setTravelReasonsError(error.message || '');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.currentState.transactionID, context.actions, history, fetchCount]);
  return {
    fetch
  };
}
